import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import portadaBg from "@/assets/portada-login.jpg";
import logoPlayGrid from "@/assets/logo-PlayGrid-feb26.png";
import { PasswordStrengthIndicator, validatePassword } from '@/components/ui/PasswordStrengthIndicator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { ArrowLeft, CalendarIcon, Mail, Lock, Eye, EyeOff, Gavel, Globe, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Separator } from '@/components/ui/separator';
import { useLanguage } from '@/contexts/LanguageContext';

const COUNTRY_CODES = [
  { id: 'MX', code: '+52', flag: '🇲🇽', name: 'México' },
  { id: 'US', code: '+1', flag: '🇺🇸', name: 'Estados Unidos' },
  { id: 'CA', code: '+1', flag: '🇨🇦', name: 'Canadá' },
  { id: 'ES', code: '+34', flag: '🇪🇸', name: 'España' },
  { id: 'AR', code: '+54', flag: '🇦🇷', name: 'Argentina' },
  { id: 'CO', code: '+57', flag: '🇨🇴', name: 'Colombia' },
  { id: 'CL', code: '+56', flag: '🇨🇱', name: 'Chile' },
  { id: 'PE', code: '+51', flag: '🇵🇪', name: 'Perú' },
  { id: 'BR', code: '+55', flag: '🇧🇷', name: 'Brasil' },
  { id: 'VE', code: '+58', flag: '🇻🇪', name: 'Venezuela' },
  { id: 'EC', code: '+593', flag: '🇪🇨', name: 'Ecuador' },
  { id: 'UY', code: '+598', flag: '🇺🇾', name: 'Uruguay' },
  { id: 'PY', code: '+595', flag: '🇵🇾', name: 'Paraguay' },
  { id: 'BO', code: '+591', flag: '🇧🇴', name: 'Bolivia' },
  { id: 'CR', code: '+506', flag: '🇨🇷', name: 'Costa Rica' },
  { id: 'PA', code: '+507', flag: '🇵🇦', name: 'Panamá' },
  { id: 'GT', code: '+502', flag: '🇬🇹', name: 'Guatemala' },
  { id: 'SV', code: '+503', flag: '🇸🇻', name: 'El Salvador' },
  { id: 'HN', code: '+504', flag: '🇭🇳', name: 'Honduras' },
  { id: 'NI', code: '+505', flag: '🇳🇮', name: 'Nicaragua' },
  { id: 'FR', code: '+33', flag: '🇫🇷', name: 'Francia' },
  { id: 'IT', code: '+39', flag: '🇮🇹', name: 'Italia' },
  { id: 'GB', code: '+44', flag: '🇬🇧', name: 'Reino Unido' },
  { id: 'DE', code: '+49', flag: '🇩🇪', name: 'Alemania' },
];

export default function EmbeddedAuth() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState<Date | undefined>(undefined);
  const [gender, setGender] = useState<string>('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+52|MX');
  const [view, setView] = useState<'signin' | 'signup' | 'forgot-password'>('signin');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t, dateLocale, language, toggleLanguage } = useLanguage();

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword(password)) {
      toast({ title: t('auth.weakPassword'), description: t('auth.weakPasswordDesc'), variant: "destructive" });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: t('auth.passwordsMismatch'), description: t('auth.passwordsMismatchDesc'), variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const dialCode = countryCode.split('|')[0];
      const trimmedPhone = phone.trim();
      let fullPhone = '';
      if (trimmedPhone) {
        if (!/^\d{10}$/.test(trimmedPhone)) {
          toast({ title: t('auth.phoneInvalid'), description: t('auth.phoneInvalidDesc'), variant: "destructive" });
          setLoading(false);
          return;
        }
        fullPhone = `${dialCode}${trimmedPhone}`;
        const { data: existingPhone } = await supabase.from('profiles').select('user_id').eq('phone', fullPhone).maybeSingle();
        if (existingPhone) {
          toast({ title: t('auth.phoneTaken'), description: t('auth.phoneTakenDesc'), variant: "destructive" });
          setLoading(false);
          return;
        }
      }
      const redirectUrl = `${window.location.origin}/`;
      const { error } = await supabase.auth.signUp({
        email, password,
        options: {
          emailRedirectTo: redirectUrl,
          data: { full_name: `${firstName} ${lastName}`.trim(), first_name: firstName, last_name: lastName, birth_date: birthDate?.toISOString().split('T')[0], gender, phone: fullPhone }
        }
      });
      if (error) { toast({ title: t('auth.signUpError'), description: error.message, variant: "destructive" }); }
      else { toast({ title: t('auth.welcome'), description: t('auth.accountCreated') }); }
    } catch (error) { toast({ title: t('common.error'), description: t('auth.unexpectedError'), variant: "destructive" }); }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth`,
      });
      if (error) throw error;
      toast({ title: language === 'es' ? 'Email enviado' : 'Email sent', description: language === 'es' ? 'Revisa tu correo para restablecer tu contraseña.' : 'Check your email to reset your password.' });
      setView('signin');
    } catch (error) {
      toast({ title: language === 'es' ? 'Error' : 'Error', description: language === 'es' ? 'No se pudo enviar el email. Inténtalo de nuevo.' : 'Could not send email. Please try again.', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) { toast({ title: t('auth.signInError'), description: error.message, variant: "destructive" }); }
      else { toast({ title: t('auth.welcome'), description: t('auth.signedIn') }); }
    } catch (error) { toast({ title: t('common.error'), description: t('auth.unexpectedError'), variant: "destructive" }); }
    finally { setLoading(false); }
  };

  const isSignUpDisabled = loading || !validatePassword(password) || password !== confirmPassword || !birthDate || !gender;

  const inputClass = "bg-white/10 border-white/20 text-white placeholder:text-white/60 focus-visible:ring-white/30 focus-visible:border-white/40";
  const labelClass = "text-white/90 text-sm font-medium";

  return (
    <div className="fixed inset-0 flex flex-col z-50 overflow-y-auto">
      <img src={portadaBg} alt="" className="fixed inset-0 w-full h-full object-cover" />
      <div className="fixed inset-0 bg-black/50" />
      {view === 'signup' && (
        <button type="button" onClick={() => setView('signin')} className="fixed text-white/70 hover:text-white p-2 z-50" style={{ top: 'max(env(safe-area-inset-top, 1rem), 1.5rem)', left: '1rem' }}>
          <ArrowLeft className="h-6 w-6" />
        </button>
      )}


      {/* Language toggle */}
      <button
        type="button"
        onClick={toggleLanguage}
        className="fixed text-white/70 hover:text-white p-2 z-50 flex items-center gap-1"
        style={{ top: 'max(env(safe-area-inset-top, 1rem), 1.5rem)', right: '1rem' }}
        title={language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
      >
        <Globe className="h-5 w-5" />
        <span className="text-xs font-semibold">{language.toUpperCase()}</span>
      </button>

      <div className="relative z-10 flex flex-col min-h-full">
        <div className={cn("flex items-center justify-center flex-shrink-0", view === 'signin' ? "pt-8 pb-2" : "pt-2 pb-2")}>
          <img src={logoPlayGrid} alt="Racket PlayGrid" className={cn("object-contain drop-shadow-2xl", view === 'signin' ? "w-48 h-48" : "w-64 h-64 -mb-24")} />
        </div>

        {view === 'signin' && <div className="flex-1" />}

        <div className={cn("px-6 w-full max-w-md mx-auto relative z-20", view === 'signin' ? "pb-2 pt-4" : "pb-16 pt-4")}>
          {view === 'signin' ? (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signin-email" className={labelClass}>{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="signin-email" type="email" placeholder={t('auth.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} required className={cn(inputClass, "pl-10")} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="signin-password" className={labelClass}>{t('auth.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="signin-password" type={showPassword ? "text" : "password"} placeholder={t('auth.passwordPlaceholder')} value={password} onChange={(e) => setPassword(e.target.value)} required className={cn(inputClass, "pl-10 pr-10")} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/70">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end -mt-1">
                <button type="button" onClick={() => setView('forgot-password')} className="text-white/60 text-xs hover:text-white/80 hover:underline">
                  {language === 'es' ? '¿Olvidaste tu contraseña?' : 'Forgot your password?'}
                </button>
              </div>
              <Button type="submit" className="w-full bg-[#e7d1b6] hover:bg-[#d4bc9e] text-gray-900 font-semibold shadow-lg transition-all duration-300 transform hover:scale-[1.02]" disabled={loading}>
                {loading ? t('auth.signingIn') : t('auth.signIn')}
              </Button>
              <p className="text-center text-white/70 text-sm mt-1">
                {t('auth.noAccount')}{' '}
                <button type="button" onClick={() => setView('signup')} className="text-[#e7d1b6] font-semibold hover:underline">{t('auth.createHere')}</button>
              </p>
            </form>
          ) : view === 'forgot-password' ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <button type="button" onClick={() => setView('signin')} className="flex items-center gap-1 text-white/60 hover:text-white/80 text-sm">
                <ArrowLeft className="h-4 w-4" /> {language === 'es' ? 'Volver' : 'Back'}
              </button>
              <p className="text-white/80 text-sm text-center">
                {language === 'es' ? 'Ingresa tu email y te enviaremos un link para restablecer tu contraseña.' : 'Enter your email and we will send you a link to reset your password.'}
              </p>
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className={labelClass}>{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="forgot-email" type="email" placeholder={t('auth.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} required className={cn(inputClass, "pl-10")} />
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#e7d1b6] hover:bg-[#d4bc9e] text-gray-900 font-semibold shadow-lg" disabled={loading || !email}>
                {loading ? (language === 'es' ? 'Enviando...' : 'Sending...') : (language === 'es' ? 'Enviar link de recuperación' : 'Send recovery link')}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSignUp} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="signup-firstname" className={labelClass}>{t('auth.firstName')}</Label>
                  <Input id="signup-firstname" type="text" placeholder={t('auth.firstNamePlaceholder')} value={firstName} onChange={(e) => setFirstName(e.target.value)} required className={cn(inputClass, "h-12")} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signup-lastname" className={labelClass}>{t('auth.lastName')}</Label>
                  <Input id="signup-lastname" type="text" placeholder={t('auth.lastNamePlaceholder')} value={lastName} onChange={(e) => setLastName(e.target.value)} required className={cn(inputClass, "h-12")} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>{t('auth.birthDate')}</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-full justify-start text-left font-normal", inputClass, !birthDate && "text-white/60")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {birthDate ? format(birthDate, "PPP", { locale: dateLocale }) : t('auth.selectDate')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={birthDate} onSelect={setBirthDate} disabled={(date) => date > new Date() || date < new Date("1900-01-01")} initialFocus captionLayout="dropdown-buttons" fromYear={1900} toYear={new Date().getFullYear()} className="pointer-events-auto" classNames={{ nav: "hidden" }} />
                    <div className="flex justify-end p-2 border-t">
                      <Button type="button" size="sm" variant="outline" onClick={() => setCalendarOpen(false)}>
                        {t('auth.ok')}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>{t('auth.gender')}</Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger className={cn("w-full", inputClass)}><SelectValue placeholder={t('auth.selectGender')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{t('auth.male')}</SelectItem>
                    <SelectItem value="female">{t('auth.female')}</SelectItem>
                    <SelectItem value="prefer_not_to_say">{t('auth.preferNotToSay')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-phone" className={labelClass}>{t('auth.phoneNumber')}</Label>
                <div className="flex gap-2">
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger className={cn("w-[110px] flex-shrink-0", inputClass)}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {COUNTRY_CODES.map((c) => (
                        <SelectItem key={c.id} value={`${c.code}|${c.id}`}>
                          <span className="mr-1">{c.flag}</span> {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                    <Input
                      id="signup-phone"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder={t('auth.phonePlaceholder')}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className={cn(inputClass, "pl-10")}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-email" className={labelClass}>{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="signup-email" type="email" placeholder={t('auth.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} required className={cn(inputClass, "pl-10")} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-password" className={labelClass}>{t('auth.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="signup-password" type={showPassword ? "text" : "password"} placeholder={t('auth.createSecurePassword')} value={password} onChange={(e) => setPassword(e.target.value)} required className={cn(inputClass, "pl-10 pr-10")} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/70">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <PasswordStrengthIndicator password={password} confirmPassword={confirmPassword} showRequirements={true} />
              <div className="space-y-1.5">
                <Label htmlFor="signup-confirm-password" className={labelClass}>{t('auth.confirmPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="signup-confirm-password" type={showConfirmPassword ? "text" : "password"} placeholder={t('auth.confirmPasswordPlaceholder')} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className={cn(inputClass, "pl-10 pr-10")} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/70">
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#e7d1b6] hover:bg-[#d4bc9e] text-gray-900 font-semibold shadow-lg transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50" disabled={isSignUpDisabled}>
                {loading ? t('auth.creatingAccount') : t('auth.signUp')}
              </Button>
              <p className="text-center text-white/70 text-sm mt-3">
                {t('auth.haveAccount')}{' '}
                <button type="button" onClick={() => setView('signin')} className="text-[#e7d1b6] font-semibold hover:underline">{t('auth.signInHere')}</button>
              </p>
            </form>
          )}
        </div>

        {view === 'signin' && (
          <div className="px-6 w-full max-w-md mx-auto relative z-20 pb-6" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 1.5rem), 1.5rem)' }}>
            <div className="flex items-center gap-3 mb-4">
              <Separator className="flex-1 bg-white/20" />
              <span className="text-white/50 text-xs">{t('auth.or')}</span>
              <Separator className="flex-1 bg-white/20" />
            </div>
            <Button
              type="button"
              onClick={() => navigate('/judge')}
              className="w-full border border-white/30 bg-transparent text-white hover:bg-white/10"
            >
              <Gavel className="w-4 h-4 mr-2" />
              {t('auth.iAmJudge')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
